import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import moment from 'moment';
import { useState } from 'react';
import { FaCalendarAlt, FaTrashAlt, FaUser } from 'react-icons/fa';
import { FaEye } from 'react-icons/fa6';
import { RxUpdate } from 'react-icons/rx';
import { toast } from 'react-toastify';
import useRetainAgreementActions from '../../../hooks/APIHooks/appointments/useRetainAgreementActions';
import { RetainAgreement } from '../../../types/retain';
import { showPicture } from '../../../utils/commonFunctions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import ConsentModal from '../Modals/ConsentModal';
import CreateEditRetainAgreementModal from '../Modals/CreateEditRetainAgreementModal';
import RetainAgreementDetailsModal from '../Modals/RetainAgreementDetailsModal';

const RetainAgreementCard = ({
  retainAgreement,
  hasDeletePermission,
  hasUpdatePermission,
}: {
  retainAgreement: RetainAgreement;
  hasDeletePermission: boolean;
  hasUpdatePermission: boolean;
}) => {
  //destructuring the retain agreement object here
  const {
    client_full_name,
    client_profile_picture,
    start_date,
    end_date,
    service_config_name,
    is_active,
  } = retainAgreement || {};

  // integration of react hooks here
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // integration of custom API hooks here
  const { deleteRetainAgreementByIdHandler } = useRetainAgreementActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  const deleteRetainAgreementHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteRetainAgreementByIdHandler
        .mutateAsync({ agreementId: retainAgreement.id })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Agreement Deleted!!');

            queryClient.invalidateQueries({
              queryKey: ['retain-agreement-list'],
            });
          } else {
            toast.error(res.data.message || 'Agreement Deletion Failed!!');
          }
        })
        .catch((error) => {
          toast.error(fetchErrorMessage(error));
        });
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  // rendering retain agreement card component here
  return (
    <>
      <div className='rounded-md shadow-academia-sm p-4'>
        <figure>
          {client_profile_picture ? (
            <div className='size-20 rounded-full mx-auto overflow-hidden border border-legal-dark-blue'>
              <img
                className='w-full h-full object-cover'
                src={showPicture(client_profile_picture)}
                alt={`avatar of ${client_full_name}`}
              />
            </div>
          ) : (
            <div className='flex items-center justify-center bg-base-light-gray size-10 rounded-full border border-legal-dark-blue'>
              <FaUser className='size-6 text-gray-300' />
            </div>
          )}

          <h4 className='text-center text-legal-dark-blue font-semibold text-xl mt-2'>
            {client_full_name}
          </h4>
        </figure>

        <figcaption className='mt-3'>
          <div className='flex items-center gap-1 text-base-purple font-semibold'>
            <FaCalendarAlt className='size-5' />
            <p>{moment(start_date).format('DD-MM-YYYY')}</p>
            <p>-</p>
            <p>{moment(end_date).format('DD-MM-YYYY')}</p>
          </div>

          <div className='flex items-start gap-1 text-legal-dark-blue italic'>
            <p>Agreement Service</p>
            <p>:</p>
            <p className='font-semibold'>{service_config_name}</p>
          </div>

          <div className='flex items-start gap-1 text-legal-dark-blue italic'>
            <p>Status</p>
            <p>:</p>
            <p
              className={`font-semibold ${
                is_active ? 'text-base-cyan' : 'text-base-red'
              }`}
            >
              {is_active ? 'Active' : 'Inactive'}
            </p>
          </div>

          <AcademiaButton
            type='button'
            onClick={() => setShowDetailsModal(true)}
            variant='premium'
            additionalClassNames='!py-1 !mt-3'
            icon={<FaEye className='size-5' />}
            fullWidth
          >
            View Details
          </AcademiaButton>

          <div className='grid grid-cols-1 2xl:grid-cols-[80px_1fr] gap-2 mt-2'>
            <AcademiaButton
              title={
                !hasDeletePermission
                  ? 'You do not have permissions!'
                  : 'Delete Agreement'
              }
              type='button'
              onClick={() => setShowDeleteModal(true)}
              variant='danger'
              additionalClassNames='!py-1 !px-4 order-2 2xl:-order-1'
              isDisabled={!hasDeletePermission}
            >
              <FaTrashAlt className='size-5' />
              <span className='block 2xl:hidden'>Delete Agreement</span>
            </AcademiaButton>

            <AcademiaButton
              title={
                !hasUpdatePermission
                  ? 'You do not have permissions!'
                  : undefined
              }
              type='button'
              onClick={() => setShowEditModal(true)}
              variant='info'
              additionalClassNames='!py-1'
              icon={<RxUpdate className='size-5' />}
              fullWidth
              isDisabled={!hasUpdatePermission}
            >
              Update Agreement
            </AcademiaButton>
          </div>
        </figcaption>
      </div>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDetailsModal && (
          <RetainAgreementDetailsModal
            title='Retain Agreement'
            retainHistory={{
              ...retainAgreement,
              start_date: moment(start_date).format('DD-MM-YYYY'),
              end_date: moment(end_date).format('DD-MM-YYYY'),
            }}
            handleModalClose={() => setShowDetailsModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showEditModal && (
          <CreateEditRetainAgreementModal
            title='update'
            retainAgreement={retainAgreement}
            handleModalClose={() => setShowEditModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this retain agreement? Once you
                delete it, you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteRetainAgreementHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default RetainAgreementCard;
